import type { QueryClient } from '@tanstack/react-query'
import {
  createRootRouteWithContext,
  Outlet,
  ScrollRestoration,
  useLocation,
} from '@tanstack/react-router'
import { lazy, Suspense, useEffect } from 'react'

import { apiClient } from '@/api/client'
import { AuthedHeader } from '@/components/shared/authed-header'
import { GlobalToaster } from '@/components/shared/global-toaster'
import type { AuthResponse, User } from '@/features/auth/types'
import { PaywallModal } from '@/features/subscriptions/components/paywall-modal'
import { useAuthStore } from '@/stores/auth-store'
import { useBackgroundStore } from '@/stores/background-store'

const TanStackRouterDevtools = import.meta.env.DEV
  ? lazy(() =>
      import('@tanstack/react-router-devtools').then((mod) => ({
        default: mod.TanStackRouterDevtools,
      })),
    )
  : () => null

export interface RouterContext {
  queryClient: QueryClient
  auth: {
    isAuthenticated: boolean
    user: User | null
  }
}

let isSessionChecked = false

export const Route = createRootRouteWithContext<RouterContext>()({
  beforeLoad: async () => {
    // On first load, try to restore session from refresh cookie
    if (!isSessionChecked) {
      isSessionChecked = true
      if (!useAuthStore.getState().accessToken) {
        try {
          const { data } = await apiClient.post<AuthResponse>('/auth/refresh')
          useAuthStore.getState().setAuth(data.user, data.access_token)
        } catch {
          // No valid session, continue as guest
        }
      }
    }

    const { isAuthenticated, user } = useAuthStore.getState()
    return { auth: { isAuthenticated, user } }
  },
  component: RootLayout,
})

// Floating account cluster appears on every logged-in route. Suppressed on
// the public landing + every auth flow (login, signup, forgot/reset password,
// email verification, OAuth callback) — those are full-screen task pages
// where a floating "you're logged in" badge would be confusing or
// contradictory.
const HEADERLESS_ROUTES = new Set<string>([
  '/',
  '/login',
  '/signup',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
  '/auth/callback',
])

const PUBLIC_METADATA: Record<string, { title: string; description: string }> =
  {
    '/': {
      title: 'Melodex | Turn Audio Into Finished Videos',
      description:
        'Create songs, narration, scene images, and finished videos in one guided workflow.',
    },
    '/pricing': {
      title: 'Pricing | Melodex',
      description:
        'Compare Melodex plans, monthly GP, video-length limits, resolutions, and voice-sample generation.',
    },
    '/privacy': {
      title: 'Privacy Policy | Melodex',
      description:
        'How Melodex collects, processes, stores, and protects your data.',
    },
    '/terms': {
      title: 'Terms of Service | Melodex',
      description: 'The terms that govern use of Melodex and generated output.',
    },
  }

function setNamedMeta(name: string, content: string) {
  let element = document.head.querySelector<HTMLMetaElement>(
    `meta[name="${name}"]`,
  )
  if (!element) {
    element = document.createElement('meta')
    element.name = name
    document.head.appendChild(element)
  }
  element.content = content
}

function setPropertyMeta(property: string, content: string) {
  let element = document.head.querySelector<HTMLMetaElement>(
    `meta[property="${property}"]`,
  )
  if (!element) {
    element = document.createElement('meta')
    element.setAttribute('property', property)
    document.head.appendChild(element)
  }
  element.content = content
}

function useRouteMetadata(pathname: string) {
  useEffect(() => {
    const metadata = PUBLIC_METADATA[pathname] ?? {
      title: `${
        pathname.split('/').filter(Boolean).at(-1)?.replaceAll('-', ' ') ??
        'App'
      } | Melodex`,
      description: 'Create and manage your Melodex projects.',
    }
    const title = metadata.title.replace(/^./, (character) =>
      character.toUpperCase(),
    )
    document.title = title
    setNamedMeta('description', metadata.description)
    setNamedMeta(
      'robots',
      PUBLIC_METADATA[pathname] ? 'index, follow' : 'noindex, nofollow',
    )
    setPropertyMeta('og:title', title)
    setPropertyMeta('og:description', metadata.description)
    setPropertyMeta('og:url', `https://melodex.app${pathname}`)

    let canonical = document.head.querySelector<HTMLLinkElement>(
      'link[rel="canonical"]',
    )
    if (!canonical) {
      canonical = document.createElement('link')
      canonical.rel = 'canonical'
      document.head.appendChild(canonical)
    }
    canonical.href = `https://melodex.app${pathname}`
  }, [pathname])
}

function RootLayout() {
  // Initialize background class on <html> from persisted store
  useBackgroundStore()

  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const { pathname } = useLocation()
  const showHeader = isAuthenticated && !HEADERLESS_ROUTES.has(pathname)
  useRouteMetadata(pathname)

  return (
    <>
      <div className="flex min-h-screen flex-col">
        {showHeader && <AuthedHeader />}
        <main className="flex-1">
          <Outlet />
        </main>
      </div>
      <ScrollRestoration />
      <GlobalToaster />
      <PaywallModal />
      <Suspense>
        <TanStackRouterDevtools position="bottom-right" />
      </Suspense>
    </>
  )
}
